import PropTypes from 'prop-types';
import {
	Box,
	Button,
	Typography,
	IconButton,
	Divider,
	TextField,
	Tooltip,
	Link,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import StatusPill from 'features/IRWorkstation/components/Common/StatusPill';
import { useNotificationContext } from 'contexts/NotificationContext';
import useSalesforceTaskUpdate from 'hooks/useSalesforceTaskUpdate';
import { DatePicker } from '@mui/x-date-pickers-pro';
import dayjs from 'dayjs';
import { useState, useEffect, useRef } from 'react';
import SingleSelect from 'components/common/SingleSelect';
import { buildCrmRecordUrl } from 'features/IRWorkstation/utils/overview';

const MAX_COMMENTS_LENGTH = 32_000;

const priorityColorMap = {
	High: 'signal.red',
	Normal: 'signal.amber',
	Low: 'signal.green',
};

const TaskField = ({ label, value, color, action, children }) => (
	<Box sx={{ mb: 2 }}>
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				mb: '4px',
			}}
		>
			<Typography
				sx={{
					fontSize: '11px',
					fontWeight: 600,
					textTransform: 'uppercase',
					color: 'text.tertiary',
				}}
			>
				{label}
			</Typography>

			{action}
		</Box>

		{children ?? (
			<Typography
				sx={{
					fontSize: '13px',
					fontWeight: 500,
					color: color || 'text.primary',
				}}
			>
				{value || '—'}
			</Typography>
		)}
	</Box>
);

TaskField.propTypes = {
	label: PropTypes.string.isRequired,
	value: PropTypes.node,
	color: PropTypes.string,
	action: PropTypes.node,
	children: PropTypes.node,
};

const SalesforceTaskDrawer = ({
	open,
	task,
	onClose,
	onTaskUpdated,
	salesforceOrgUrl,
}) => {
	const { showNotification } = useNotificationContext();
	const { updateTask } = useSalesforceTaskUpdate();
	const commentsInputRef = useRef(null);

	const [statusLoading, setStatusLoading] = useState(false);
	const [dueDateLoading, setDueDateLoading] = useState(false);
	const [commentsLoading, setCommentsLoading] = useState(false);

	// Currently selected due date in the DatePicker
	const [dueDate, setDueDate] = useState(null);
	// Last saved due date used to detect unsaved changes
	const [savedDueDate, setSavedDueDate] = useState(null);
	// Tracks whether the user has edited the due date
	const [hasChangedDueDate, setHasChangedDueDate] = useState(false);

	// Comments state
	const [isEditingComments, setIsEditingComments] = useState(false);
	const [comments, setComments] = useState('');
	const [savedComments, setSavedComments] = useState('');

	const trimComments = (value) => value?.trim() || '';

	const showCompletedBanner =
		task?.status === 'Completed' &&
		task?.completed_at &&
		dayjs(task.completed_at).isSame(dayjs(), 'day');

	// Resets the drawer state
	const initializeDrawerState = (currentTask) => {
		const taskDueDate = currentTask?.due_date ?? null;
		const taskComments = trimComments(currentTask?.comments);

		setDueDate(taskDueDate ? dayjs(taskDueDate) : null);
		setSavedDueDate(taskDueDate);
		setHasChangedDueDate(false);

		setComments(taskComments);
		setSavedComments(taskComments);
		setIsEditingComments(false);
	};

	useEffect(() => {
		initializeDrawerState(task);
	}, [task]);

	// Place the cursor at the end of the existing comments when entering edit mode
	useEffect(() => {
		if (!isEditingComments) return;

		const textarea = commentsInputRef.current;
		if (!textarea) return;

		const length = textarea.value.length;
		textarea.focus();
		textarea.setSelectionRange(length, length);
	}, [isEditingComments]);

	// -------------------------------------------------------------------------
	// Due date
	// -------------------------------------------------------------------------

	const handleDueDateChange = (value) => {
		setHasChangedDueDate(true);
		setDueDate(value);
	};

	// Due date validation boundaries
	const minDueDate = dayjs().startOf('day');
	const maxDueDate = dayjs().add(1, 'year').endOf('day');

	const isEmptyDueDate = !dueDate;
	const isInvalidDueDate = dueDate && !dueDate.isValid();

	const currentDueDate = dueDate?.isValid()
		? dueDate.format('YYYY-MM-DD')
		: null;

	// Prevent unnecessary updates when the due date has not changed
	const isUnchangedDueDate = currentDueDate === savedDueDate;

	// Allow viewing an existing past due date, but prevent changing to a new past date
	const isPastDueDate =
		dueDate?.isValid() &&
		dueDate.isBefore(minDueDate, 'day') &&
		dueDate.format('YYYY-MM-DD') !== task?.due_date;

	// Prevent selecting a due date more than one year in the future
	const exceedsMaxDueDate =
		dueDate?.isValid() && dueDate.isAfter(maxDueDate, 'day');

	const isDueDateInvalid =
		isEmptyDueDate || isInvalidDueDate || isPastDueDate || exceedsMaxDueDate;

	const dueDateHelperText = isEmptyDueDate
		? 'Due date is required'
		: isInvalidDueDate
			? 'Please enter a valid due date'
			: isPastDueDate
				? 'Due date cannot be in the past'
				: exceedsMaxDueDate
					? 'Due date cannot be more than 1 year in the future'
					: undefined;

	// Persist the updated due date to Salesforce and refresh task data on success
	const handleUpdateDueDate = async () => {
		if (!task || isDueDateInvalid) return;
		const formattedDate = dueDate.format('YYYY-MM-DD');
		setDueDateLoading(true);

		try {
			const updatedTask = await updateTask({
				taskId: task.id,
				due_date: formattedDate,
			});

			// Sync local state with the saved value so the button disables immediately
			setSavedDueDate(updatedTask.due_date);
			setDueDate(dayjs(updatedTask.due_date));
			setHasChangedDueDate(false);

			await onTaskUpdated(updatedTask);

			showNotification(
				`Task due date changed to ${updatedTask.due_date}`,
				'success'
			);
		} catch (err) {
			// Revert to the last saved value
			setDueDate(savedDueDate ? dayjs(savedDueDate) : null);

			showNotification(
				err.message || 'Unable to update task due date',
				'error'
			);
		} finally {
			setDueDateLoading(false);
		}
	};

	// -------------------------------------------------------------------------
	// Status
	// -------------------------------------------------------------------------

	const handleUpdateStatus = async (status) => {
		if (!task || status === task.status) return;
		setStatusLoading(true);

		try {
			const updatedTask = await updateTask({
				taskId: task.id,
				status,
			});

			await onTaskUpdated(updatedTask);

			showNotification(
				`Task status changed to ${updatedTask.status}`,
				'success'
			);
		} catch (err) {
			showNotification(err.message || 'Unable to update task status', 'error');

			throw err;
		} finally {
			setStatusLoading(false);
		}
	};

	// -------------------------------------------------------------------------
	// Comments
	// -------------------------------------------------------------------------

	const trimmedComments = comments.trim();
	const commentsLength = trimmedComments.length;
	const isCommentsTooLong = commentsLength > MAX_COMMENTS_LENGTH;
	const isCommentsUnchanged =
		trimComments(comments) === trimComments(savedComments);

	const handleStartEditingComments = () => {
		if (commentsLoading) return;

		setIsEditingComments(true);
	};

	const handleCommentsChange = (event) => {
		setComments(event.target.value);
	};

	const handleCancelCommentsEdit = () => {
		if (commentsLoading) return;

		setComments(savedComments);
		setIsEditingComments(false);
	};

	const handleUpdateComments = async () => {
		if (!task || isCommentsTooLong || isCommentsUnchanged) return;

		setCommentsLoading(true);

		try {
			const commentsToSave = trimComments(comments);

			const updatedTask = await updateTask({
				taskId: task.id,
				comments: commentsToSave,
			});

			const updatedComments = trimComments(updatedTask.comments);

			setComments(updatedComments);
			setSavedComments(updatedComments);
			setIsEditingComments(false);

			await onTaskUpdated(updatedTask);

			showNotification('Task comments updated successfully', 'success');
		} catch (err) {
			showNotification(
				err.message || 'Unable to update task comments',
				'error'
			);
		} finally {
			setCommentsLoading(false);
		}
	};

	// ----
	// Build the url
	// ----

	const renderCrmLink = (label, text, recordId) => {
		const url = buildCrmRecordUrl(salesforceOrgUrl, recordId);

		if (!url) {
			return <TaskField label={label} value={text} />;
		}

		return (
			<TaskField label={label}>
				<Link
					href={url}
					target='_blank'
					rel='noopener noreferrer'
					sx={{
						fontSize: '13px',
						fontWeight: 500,
						color: 'primary.main',
						textDecoration: 'none',
						'&:hover': { textDecoration: 'underline' },
					}}
				>
					{text || '—'}
				</Link>
			</TaskField>
		);
	};

	// -------------------------------------------------------------------------
	// Drawer
	// -------------------------------------------------------------------------

	const handleCloseDrawer = () => {
		initializeDrawerState(task);
		onClose();
	};

	return (
		<>
			{/* backdrop */}
			<Box
				onClick={handleCloseDrawer}
				sx={{
					position: 'absolute',
					inset: 0,
					backgroundColor: 'rgba(0,0,0,0.25)',
					opacity: open ? 1 : 0,
					pointerEvents: open ? 'auto' : 'none',
					transition: 'opacity 225ms cubic-bezier(0.4, 0, 0.2, 1)',
					zIndex: 1200,
				}}
			/>

			{/* panel */}
			<Box
				sx={{
					position: 'absolute',
					top: 0,
					right: 0,
					bottom: 0,
					width: 480,
					backgroundColor: 'background.paper',
					transform: open ? 'translateX(0)' : 'translateX(100%)',
					transition: 'transform 225ms cubic-bezier(0, 0, 0.2, 1)',
					zIndex: 1201,
					display: 'flex',
					flexDirection: 'column',
					boxShadow: open ? '-4px 0 20px rgba(0,0,0,0.08)' : 'none',
				}}
			>
				{/* header */}
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'flex-start',
						padding: '20px 24px',
						borderBottom: '1px solid',
						borderColor: 'border.light',
						backgroundColor: 'background.subtle',
					}}
				>
					{!task ? (
						<Box sx={{ py: 2 }}>
							<Typography sx={{ color: 'text.secondary' }}>
								No task selected
							</Typography>
						</Box>
					) : (
						<Box>
							<Box
								sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
							>
								<StatusPill label='CRM Task' color='blue' />
								<Typography
									sx={{
										fontSize: '11px',
										color: priorityColorMap[task.priority] || 'text.secondary',
										fontWeight: 700,
										textTransform: 'uppercase',
										marginLeft: '8px',
									}}
								>
									{task.priority} Priority
								</Typography>
							</Box>
							<Typography
								sx={{ fontSize: '18px', fontWeight: 700, lineHeight: 1.3 }}
							>
								{task.subject}
							</Typography>
						</Box>
					)}
					<IconButton
						onClick={onClose}
						sx={{
							mt: -0.5,
							mr: -1,
							color: 'text.tertiary',
							'&:hover': { backgroundColor: 'background.subtle' },
						}}
					>
						<CloseIcon />
					</IconButton>
				</Box>

				{/* Completed Today banner */}
				{showCompletedBanner && (
					<Box
						sx={{
							mx: 3,
							mt: 3,
							mb: 0,
							px: 2,
							py: 1.5,
							display: 'flex',
							alignItems: 'center',
							gap: 1.5,
							borderRadius: 1, // 8px instead of 12px
							backgroundColor: 'status.green.bg',
							border: '1px solid',
							borderColor: 'signal.green',
						}}
					>
						<DoneIcon
							sx={{
								color: 'signal.green',
								fontSize: 22,
								flexShrink: 0,
							}}
						/>

						<Box>
							<Typography
								sx={{
									fontSize: '14px',
									fontWeight: 700,
									lineHeight: 1.2,
									color: 'status.green.text',
								}}
							>
								Completed today
							</Typography>

							<Typography
								sx={{
									mt: 0.25,
									fontSize: '12px',
									lineHeight: 1.3,
									color: 'text.secondary',
								}}
							>
								Marked complete at {dayjs(task.completed_at).format('h:mm A')}
							</Typography>
						</Box>
					</Box>
				)}

				{/* body */}
				{task && (
					<Box sx={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
						<Box
							sx={{
								display: 'grid',
								gridTemplateColumns: '1fr 1fr',
								gap: '16px',
								mb: 4,
							}}
						>
							<TaskField label='Status'>
								<SingleSelect
									value={task.status}
									options={task.statuses ?? []}
									loading={statusLoading}
									placeholder='Select a status'
									onChange={handleUpdateStatus}
								/>
							</TaskField>
							{/* editable DatePicker */}
							<TaskField label='Due Date'>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
									<DatePicker
										value={dueDate}
										onChange={handleDueDateChange}
										disabled={dueDateLoading}
										minDate={minDueDate}
										maxDate={maxDueDate}
										slotProps={{
											textField: {
												size: 'small',
												fullWidth: true,
												error: hasChangedDueDate && isDueDateInvalid,
												helperText: hasChangedDueDate
													? dueDateHelperText
													: undefined,
											},
										}}
									/>
								</Box>

								<Box
									sx={{
										display: 'flex',
										justifyContent: 'flex-end',
										mt: 1.5,
									}}
								>
									<LoadingButton
										data-testid='change-date-button'
										variant='contained'
										size='small'
										loadingPosition='start'
										loading={dueDateLoading}
										disabled={
											dueDateLoading ||
											isUnchangedDueDate ||
											(hasChangedDueDate && isDueDateInvalid)
										}
										onClick={handleUpdateDueDate}
										sx={{ minWidth: 140 }}
									>
										Change Date
									</LoadingButton>
								</Box>
							</TaskField>

							{renderCrmLink('Related To', task.related_to, task.related_to_id)}
							{renderCrmLink('Name', task.name, task.name_id)}
						</Box>

						<Divider sx={{ mb: 3 }} />

						{/* Editable comments */}
						<TaskField
							label='Comments'
							action={
								!isEditingComments && (
									<Tooltip title='Edit comments'>
										<IconButton
											size='small'
											aria-label='Task comments'
											onClick={handleStartEditingComments}
											sx={{
												color: 'text.secondary',
												'&:hover': {
													color: 'primary.main',
													backgroundColor: 'background.subtle',
												},
											}}
										>
											<EditOutlinedIcon fontSize='small' />
										</IconButton>
									</Tooltip>
								)
							}
						>
							{isEditingComments ? (
								<Box>
									<TextField
										inputRef={commentsInputRef}
										autoFocus
										fullWidth
										multiline
										minRows={5}
										maxRows={12}
										value={comments}
										onChange={handleCommentsChange}
										disabled={commentsLoading}
										error={isCommentsTooLong}
										placeholder='Add task comments...'
										helperText={
											isCommentsTooLong
												? `Comments cannot exceed ${MAX_COMMENTS_LENGTH.toLocaleString()} characters`
												: `${commentsLength.toLocaleString()} / ${MAX_COMMENTS_LENGTH.toLocaleString()}`
										}
										slotProps={{
											htmlInput: {
												'data-testid': 'task-comments-input',
												maxLength: MAX_COMMENTS_LENGTH + 1,
											},
										}}
										sx={{
											'& .MuiInputBase-root': {
												alignItems: 'flex-start',
											},
											'& textarea': {
												maxHeight: 300,
												overflowY: 'auto',
											},
										}}
									/>

									<Box
										sx={{
											display: 'flex',
											justifyContent: 'flex-end',
											gap: 1,
											mt: 1.5,
										}}
									>
										<LoadingButton
											variant='text'
											size='small'
											disabled={commentsLoading}
											onClick={handleCancelCommentsEdit}
										>
											Cancel
										</LoadingButton>

										<LoadingButton
											data-testid='save-task-comments-button'
											variant='contained'
											size='small'
											loading={commentsLoading}
											disabled={
												commentsLoading ||
												isCommentsTooLong ||
												isCommentsUnchanged
											}
											onClick={handleUpdateComments}
										>
											Save
										</LoadingButton>
									</Box>
								</Box>
							) : (
								<Box
									role='button'
									tabIndex={0}
									aria-label='Edit comments'
									onClick={handleStartEditingComments}
									onKeyDown={(event) => {
										if (event.key === 'Enter' || event.key === ' ') {
											event.preventDefault();
											handleStartEditingComments();
										}
									}}
									sx={{
										position: 'relative',
										backgroundColor: 'background.subtle',
										borderRadius: '8px',
										padding: '16px',
										border: '1px solid',
										borderColor: 'border.light',
										cursor: 'pointer',
										transition:
											'border-color 150ms ease, background-color 150ms ease',
										'&:hover': {
											borderColor: 'primary.main',
											backgroundColor: 'action.hover',
										},
										'&:focus-visible': {
											outline: '2px solid',
											outlineColor: 'primary.main',
											outlineOffset: '2px',
										},
									}}
								>
									<Typography
										sx={{
											fontSize: '13px',
											fontWeight: 500,
											color: 'text.primary',
											whiteSpace: 'pre-wrap',
											overflowWrap: 'anywhere',
											maxHeight: 240,
											overflowY: 'auto',
											pr: 1,
										}}
									>
										{comments}
									</Typography>
								</Box>
							)}
						</TaskField>

						<Box
							sx={{
								display: 'grid',
								gridTemplateColumns: '1fr 1fr',
								gap: '16px',
							}}
						>
							<TaskField label='Created By' value={task.created_by} />
							<TaskField
								label='Last Modified By'
								value={task.last_modified_by}
							/>
						</Box>
						{/* CRM action */}
						{task?.action_link && (
							<Button
								href={task.action_link}
								target='_blank'
								rel='noopener noreferrer'
								variant='outlined'
								size='small'
								sx={{
									mt: 2,
									fontSize: '12px',
									fontWeight: 500,
									padding: '7px 14px',
									borderRadius: '6px',
									backgroundColor: 'status.blue.bg',
									color: 'status.blue.text',
									borderColor: 'status.blue.border',
									'&:hover': {
										backgroundColor: 'status.blue.hover',
									},
								}}
							>
								View Task in CRM
							</Button>
						)}
					</Box>
				)}
			</Box>
		</>
	);
};

SalesforceTaskDrawer.propTypes = {
	open: PropTypes.bool.isRequired,
	task: PropTypes.object,
	onClose: PropTypes.func.isRequired,
	onTaskUpdated: PropTypes.func.isRequired,
	salesforceOrgUrl: PropTypes.string,
};

export default SalesforceTaskDrawer;
